import { useMutation } from "@apollo/client";
import {
  GET_ACTIVE_ORDER,
  GET_TOTAL_LINES,
} from "../../../queries/cart.queries";
import { GET_ACTIVE_USER } from "../../../queries/auth.queries";
import { CANCEL_ORDER } from "../../../mutations/cancel-order";

interface CancelOrderMutationVariables {
  id: string;
}

interface CancelOrderMutation {
  cancelOpenOrder: {
    __typename: string;
  };
}

export interface CancelOrderResult {
  ok: boolean;
  type: "success" | "network" | "graphql" | "unknown";
  message: string;
}

export const useCancelOrder = () => {
  const [mutateCancel, { loading }] = useMutation<
    CancelOrderMutation,
    CancelOrderMutationVariables
  >(CANCEL_ORDER, {
    awaitRefetchQueries: true,
    refetchQueries: [
      { query: GET_TOTAL_LINES },
      { query: GET_ACTIVE_ORDER },
      { query: GET_ACTIVE_USER },
    ],
  });

  const cancelOrder = async (orderId: string): Promise<CancelOrderResult> => {
    try {
      const res = await mutateCancel({
        variables: { id: orderId },
      });

      // No data returned
      if (!res.data) {
        return {
          ok: false,
          type: "unknown",
          message: "No response received from server.",
        };
      }

      // Validate response type
      if (res.data.cancelOpenOrder?.__typename === "Order") {
        return {
          ok: true,
          type: "success",
          message: "Order successfully cancelled.",
        };
      }

      // GraphQL-level problem (but not network)
      return {
        ok: false,
        type: "graphql",
        message: "Unexpected response while cancelling order.",
      };
    } catch (e: any) {
      return {
        ok: false,
        type: "network",
        message: e?.message ?? "Network error while cancelling order.",
      };
    }
  };

  return {
    cancelOrder,
    loading,
  };
};
